import React, { useState, useEffect, useRef, useContext } from 'react';
import { Box, IconButton, styled, useTheme } from '@mui/material';
import { North, South, East, West, Navigation } from '@mui/icons-material';
import { SizeContext } from '../../../context/handleContext';

const AnalogScrollControl = ({ onScroll }) => {
  const theme = useTheme();
  const [size] = useContext(SizeContext)
  const [isDragging, setIsDragging] = useState(false);
  const [direction, setDirection] = useState(null);
  const [delta, setDelta] = useState(0);
  const [lastScrollTime, setLastScrollTime] = useState(0);
  const containerRef = useRef(null);
  const requestRef = useRef();
  const deltaAcceleration = useRef(1);
  const accelerationInterval = useRef();
  const touchIdRef = useRef(null);
  
  const MAX_DELTA = 300;
  const MIN_DELTA = 20;
  const ACCELERATION_RATE = 1.2;
  const DECELERATION_RATE = 0.95;
  const PRECISE_SCROLL_DELTA = 300;
  const SCROLL_INTERVAL = 50; // ms

  const AnalogContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: 200,
    height: 200,
    borderRadius: '50%',
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(66, 66, 66, 0.2)' 
      : 'rgba(25, 118, 210, 0.1)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: theme.shadows[4],
    border: `1px solid ${theme.palette.divider}`,
    touchAction: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[6],
    },
  }));

  const AnalogStick = styled(Box)(({ theme, $delta, $direction }) => ({
    position: 'absolute',
    width: 60,
    height: 60,
    borderRadius: '50%',
    backgroundColor: theme.palette.mode === 'dark' 
      ? theme.palette.primary.main 
      : theme.palette.primary.light,
    transform: $direction
      ? `translate(${$direction === 'left' ? -$delta * 0.5 : $direction === 'right' ? $delta * 0.5 : 0}px, 
                  ${$direction === 'up' ? -$delta * 0.5 : $direction === 'down' ? $delta * 0.5 : 0}px)`
      : 'none',
    transition: !isDragging ? 'all 0.2s ease-out' : 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.getContrastText(
      theme.palette.mode === 'dark' 
        ? theme.palette.primary.main 
        : theme.palette.primary.light
    ),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      transform: $direction
        ? `translate(${$direction === 'left' ? -$delta * 0.5 : $direction === 'right' ? $delta * 0.5 : 0}px, 
                    ${$direction === 'up' ? -$delta * 0.5 : $direction === 'down' ? $delta * 0.5 : 0}px) scale(1.05)`
        : 'scale(1.05)',
    },
  }));

  const ArrowButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(97, 97, 97, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)',
    color: theme.palette.mode === 'dark' 
      ? theme.palette.getContrastText('#616161') 
      : theme.palette.text.primary,
    boxShadow: theme.shadows[2],
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? theme.palette.primary.dark 
        : theme.palette.primary.light
    },
    transition: 'all 0.2s ease',
  }));

  useEffect(() => {
    if (isDragging && direction) {
      accelerationInterval.current = setInterval(() => {
        deltaAcceleration.current = Math.min(deltaAcceleration.current * ACCELERATION_RATE, 5);
      }, 500);
    } else {
      deltaAcceleration.current = 1;
      clearInterval(accelerationInterval.current);
    }

    return () => clearInterval(accelerationInterval.current);
  }, [isDragging, direction]);

  useEffect(() => {
    if (!direction || !isDragging) {
      cancelAnimationFrame(requestRef.current);
      return;
    }

    const scroll = () => {
      try {
        const now = Date.now();
        if (now - lastScrollTime >= SCROLL_INTERVAL && direction) {
          const rawDelta = delta * deltaAcceleration.current;
          const calculatedDelta = Math.min(
            Math.max(Math.floor(rawDelta), MIN_DELTA),
            MAX_DELTA
          );
          
          onScroll?.({ direction, delta: calculatedDelta });
          setLastScrollTime(now);
        }
        requestRef.current = requestAnimationFrame(scroll);
      } catch (error) {
        console.error("Scroll error:", error);
        cancelAnimationFrame(requestRef.current);
      }
    };

    requestRef.current = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(requestRef.current);
  }, [direction, delta, isDragging, lastScrollTime, onScroll]);

  const handleStart = (e) => {
    if ( size.width <= 700 ) return 
    e.preventDefault();
    setIsDragging(true);
  
    if (e.touches) {
      const touch = e.touches[0];
      touchIdRef.current = touch.identifier;
      updateDirectionAndDelta(touch.clientX, touch.clientY);
    } else {
      updateDirectionAndDelta(e.clientX, e.clientY);
    }
  };

  const handleMove = (e) => {
    if (!isDragging) return;
    e.preventDefault()
  
    let clientX, clientY;
  
    if (e.touches) {
      const touch = Array.from(e.touches).find(t => t.identifier === touchIdRef.current);
      if (!touch) return;
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
  
    updateDirectionAndDelta(clientX, clientY);
  };

  const handleEnd = (e) => {
    e.preventDefault()
    // Verifica se é um evento de touch e se o touchId corresponde
    if (e.touches && touchIdRef.current !== null) {
      const remainingTouches = Array.from(e.touches).filter(
        t => t.identifier !== touchIdRef.current
      );
      
      // Se ainda há toques no elemento, não encerra o arrasto
      if (remainingTouches.length > 0) {
        const newTouch = remainingTouches[0];
        touchIdRef.current = newTouch.identifier;
        updateDirectionAndDelta(newTouch.clientX, newTouch.clientY);
        return;
      }
    }
    
    // Caso contrário, encerra o arrasto
    setIsDragging(false);
    setDirection(null);
    setDelta(0);
    touchIdRef.current = null;
    cancelAnimationFrame(requestRef.current);
  };

  const updateDirectionAndDelta = (clientX, clientY) => {
    if (!containerRef.current) return;
  
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
  
    const x = clientX - centerX;
    const y = clientY - centerY;
    const distance = Math.min(Math.sqrt(x * x + y * y), rect.width / 2);
  
    let newDirection;
    if (Math.abs(x) > Math.abs(y)) {
      newDirection = x > 0 ? 'right' : 'left';
    } else {
      newDirection = y > 0 ? 'down' : 'up';
    }
  
    const newDelta = Math.max(MIN_DELTA, Math.min(MAX_DELTA, distance));
  
    setDirection(newDirection);
    setDelta(newDelta);
  };

  const handlePreciseScroll = (dir) => {
    onScroll?.({ direction: dir, delta: PRECISE_SCROLL_DELTA });
  };

  return (
    <Box sx={{ 
      position: 'relative', 
      width: 240, 
      height: 240, 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      p: 2,
      borderRadius: 2,
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(33, 33, 33, 0.5)' 
        : 'rgba(245, 245, 245, 0.5)',
    }}>
      <AnalogContainer
        ref={containerRef}
      >
        <AnalogStick 
        $delta={delta} 
        $direction={direction}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
        onTouchCancel={handleEnd}
        >
          <Navigation sx={{ 
            transform: direction 
              ? `rotate(${direction === 'up' ? 0 : direction === 'right' ? 90 : direction === 'down' ? 180 : 270}deg)`
              : 'none',
            transition: 'transform 0.2s ease-out'
          }} />
        </AnalogStick>
        
        <ArrowButton 
          sx={{ top: 8, left: '50%', transform: 'translateX(-50%)' }}
          onClick={() => handlePreciseScroll('up')}
          aria-label="Scroll up"
        >
          <North />
        </ArrowButton>
        
        <ArrowButton 
          sx={{ bottom: 8, left: '50%', transform: 'translateX(-50%)' }}
          onClick={() => handlePreciseScroll('down')}
          aria-label="Scroll down"
        >
          <South />
        </ArrowButton>
        
        <ArrowButton 
          sx={{ left: 8, top: '50%', transform: 'translateY(-50%)' }}
          onClick={() => handlePreciseScroll('left')}
          aria-label="Scroll left"
        >
          <West />
        </ArrowButton>
        
        <ArrowButton 
          sx={{ right: 8, top: '50%', transform: 'translateY(-50%)' }}
          onClick={() => handlePreciseScroll('right')}
          aria-label="Scroll right"
        >
          <East />
        </ArrowButton>
      </AnalogContainer>
    </Box>
  );
};

export default AnalogScrollControl;